import React, { useCallback, useRef } from 'react'
import { Box, Stack, Typography, styled } from '@mui/material'
import { RawAudioMessage } from '@weblite/ts-action/message'
import { playAudio, pauseAudio, useAudioState } from '@weblite/audio'
import { PlayIcon } from '$common/icons/play'
import { PauseIcon } from '$common/icons/pause'
import { ActionHandler, ActionHandlerState } from '$common/action-handler'
import { useSeek, useContentFileStatus } from '$common/react-hooks/message'
import { getFileTitle, formatFileSize } from '$func/file'
import { formatDuration } from '$func/time'
import * as audioPlayerService from '$logic/audio-player'

const Container = styled(Stack)`
  flex-grow: 1;
  position: relative;
  flex-direction: row;
  align-items: center;

  padding: 10px 0;
  box-sizing: border-box;
  overflow: hidden;
`

const ContentContainer = styled(Box)`
  flex-grow: 1;
  margin-right: 10px;
  overflow: hidden;
  height: 36px;
`

const AudioMetaTypography = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  fontSize: 11,
  color: theme.palette.gray,
  lineHeight: '12px',
}))

const playStateToIconMap = {
  NOT_PLAYING: PlayIcon,
  PLAYING: PauseIcon,
  PAUSED: PlayIcon,
}

export function Audio({
  attachment,
  selected,
  selectOnTap,
  roomId,
  iid,
}: {
  attachment: RawAudioMessage['attachment']
  selected: boolean
  selectOnTap: boolean
  iid: number
  roomId: string
}) {
  const { duration, fileId, filename, cover, size } = attachment

  const {
    state,
    percentage,
    blobURL,
    cancelOperation,
    startDownload,
  } = useContentFileStatus({ fileId, uploadId: null, messageType: 'AUDIO' })

  const playState = useAudioState(blobURL)
  const containerRef = useRef<HTMLDivElement>(null)
  const seekContainerRef = useRef<HTMLDivElement>(null)

  const canSeek = state === 'AVAILABLE'

  const seekValue = useSeek({
    src: blobURL,
    canSeek,
    containerRef,
    seekContainerRef,
  })

  const downloadingOrUploading =
    state === 'UPLOADING' || state === 'DOWNLOADING'

  const { sizeText: fileSizeText, unit: fileSizeUnit } = formatFileSize(size)
  const { sizeText: progressedSizeText } = formatFileSize(
    Math.round(size * (percentage / 100)),
    fileSizeUnit,
    false,
  )

  // TODO: Use HostUrl
  const coverUrl = cover

  const audioTitle = getFileTitle(filename)
  const audioDurationText = formatDuration(duration)

  // in case of playing
  const spentDurationText = formatDuration(duration * seekValue)

  const CustomActionIcon =
    state === 'AVAILABLE' ? playStateToIconMap[playState] : undefined

  const togglePlay = useCallback(() => {
    if (playState === 'PLAYING') pauseAudio(blobURL)
    else {
      audioPlayerService.setData(roomId, iid)
      playAudio(blobURL, duration / 1000, { name: audioTitle, type: 'AUDIO' })
    }
  }, [playState, blobURL, duration])

  return (
    <Container ref={containerRef}>
      <ActionHandler
        state={state as ActionHandlerState}
        percentage={percentage}
        onCancelClick={cancelOperation}
        onDownloadClick={startDownload}
        onCustomActionIconClick={togglePlay}
        customActionIconComp={CustomActionIcon && <CustomActionIcon />}
        coverUrl={coverUrl}
        selected={selected}
        selectOnTap={selectOnTap}
      />

      <ContentContainer>
        <Typography
          color="semiBlack"
          variant="caption1"
          sx={{ display: 'block', lineHeight: '16px' }}
          noWrap
        >
          {audioTitle}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          sx={{ overflow: 'hidden', marginTop: '8px' }}
        >
          {!downloadingOrUploading && (
            <>
              <AudioMetaTypography>{audioDurationText}</AudioMetaTypography>
              {playState !== 'NOT_PLAYING' && (
                <AudioMetaTypography
                  sx={{
                    color: '#c4c4c4',
                    margin: '0 4px',
                  }}
                >
                  /
                </AudioMetaTypography>
              )}
            </>
          )}

          {(playState === 'PLAYING' || playState === 'PAUSED') && (
            <AudioMetaTypography>{spentDurationText}</AudioMetaTypography>
          )}
          {(!downloadingOrUploading ||
            playState === 'PLAYING' ||
            playState === 'PAUSED') &&
            state !== 'AVAILABLE' && (
              <AudioMetaTypography sx={{ marginX: '4px' }}>
                /
              </AudioMetaTypography>
            )}

          {state !== 'AVAILABLE' && (
            <AudioMetaTypography
              noWrap
              sx={{
                color: downloadingOrUploading ? 'primary.main' : 'gray',
              }}
            >
              {downloadingOrUploading
                ? `${progressedSizeText} از ${fileSizeText}`
                : fileSizeText}
            </AudioMetaTypography>
          )}
        </Stack>
      </ContentContainer>
    </Container>
  )
}
